import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "The debt-to-equity ratio (D/E) measures how much of a company is funded by debt versus shareholder equity. Formula: Total Debt ÷ Shareholder Equity. A D/E of 1.0 means the company has equal debt and equity. Above 2.0 is high for most industries; banks and utilities run higher by design. D/E is the quickest single check on financial risk.";

const FAQS = [
  { q: 'What is a "good" debt-to-equity ratio?', a: 'For most non-financial companies, under 1.0 is conservative, 1.0–2.0 is moderate, above 2.0 starts to look risky. Utilities and REITs commonly run above 2.0 by industry design. Banks operate at much higher leverage and use different ratios entirely.' },
  { q: 'Should I use total debt or only long-term debt?', a: 'Most analysts use total debt (short-term plus long-term). Some prefer net debt (total debt minus cash) because cash can be used to pay debt down. Both are defensible.' },
  { q: 'Can a company have zero debt?', a: 'Yes. Many large software companies (until recently) ran with no net debt. Berkshire Hathaway operates conservatively. Zero debt isn\'t always optimal — modest leverage can boost ROE.' },
  { q: 'Is more debt always bad?', a: 'No. Cheap debt used to fund profitable projects can boost shareholder returns. The danger is debt that grows faster than profits, especially when interest rates rise.' },
  { q: 'How is D/E different from the debt ratio?', a: 'The debt ratio is total debt ÷ total assets. D/E is total debt ÷ equity. Both measure leverage, but D/E focuses on the mix between debt and equity capital.' },
];

export default function DebtToEquityRatioPage() {
  return (
    <BlogArticleLayout
      slug="debt-to-equity-ratio"
      title="What Is the Debt-to-Equity Ratio?"
      description="D/E ratio measures how much of a company is funded by debt. Formula, sector benchmarks, and the warning signs to watch."
      keywords="debt to equity ratio, D/E ratio, what is debt to equity, debt to equity formula, good debt to equity ratio, leverage ratio, financial leverage"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-roe', 'how-to-read-a-balance-sheet', 'investing-mistakes-beginners-make']}
    >
      <section>
        <h2>What is the debt-to-equity ratio?</h2>
        <p>
          The D/E ratio answers: of every dollar of capital this company uses, how much came from lenders versus shareholders?
          Formula: <strong>D/E = Total Debt ÷ Shareholder Equity</strong>.
        </p>
        <p>
          A D/E of 0.5 means the company has 50 cents of debt for every $1 of equity. A D/E of 2.0 means $2 of debt for every $1 of equity — a much more leveraged structure that amplifies both gains and losses.
        </p>
      </section>

      <section>
        <h2>How do you calculate D/E?</h2>
        <p>
          Two numbers, both on the balance sheet:
        </p>
        <ul>
          <li><strong>Total debt</strong> — short-term debt + long-term debt. Some analysts also include lease obligations.</li>
          <li><strong>Shareholder equity</strong> — total equity at the bottom of the balance sheet.</li>
        </ul>
        <p>
          Worked example: Coca-Cola has roughly $40B of total debt and $25B of equity. D/E ≈ 40 ÷ 25 = <strong>1.6</strong>. Higher than average, but supported by very stable cash flow.
        </p>
      </section>

      <section>
        <h2>What's "normal" D/E by sector?</h2>
        <ul>
          <li><strong>Software / Tech</strong> — usually under 0.5. Asset-light businesses don't need much debt.</li>
          <li><strong>Branded consumer</strong> — often 1.0–2.0. Stable cash flow supports moderate leverage.</li>
          <li><strong>Utilities</strong> — 1.5–3.0. Capital-intensive but predictable revenue.</li>
          <li><strong>REITs</strong> — typically 1.0–3.0. Real estate is built on debt by design.</li>
          <li><strong>Banks</strong> — 5x+. Banks borrow to lend. Don't compare them with D/E; use Tier 1 capital ratios instead.</li>
        </ul>
        <p>
          A high D/E in a stable sector (utilities) is fine. A high D/E in a volatile sector (autos, airlines) is a red flag.
        </p>
      </section>

      <section>
        <h2>Why does leverage matter?</h2>
        <p>
          Debt has a fixed cost: interest payments are owed regardless of how the business is doing. In good times, leverage amplifies returns to shareholders. In bad times, it can wipe equity out.
          The 2008 financial crisis bankrupted many highly leveraged companies that would have survived with less debt.
        </p>
        <p>
          Pair D/E with two other checks:
        </p>
        <ul>
          <li><strong>Interest coverage ratio</strong> = Operating Income ÷ Interest Expense. Should be at least 3x.</li>
          <li><strong>Free cash flow trend</strong> — see our guide on <Link to="/blog/what-is-free-cash-flow">free cash flow</Link>.</li>
        </ul>
      </section>

      <section>
        <h2>How does D/E fit into stock picking?</h2>
        <p>
          Two practical uses:
        </p>
        <ul>
          <li><strong>Cross-check ROE.</strong> A 25% <Link to="/blog/what-is-roe">ROE</Link> built on a 3.0 D/E is largely a leverage story. Same ROE on 0.3 D/E points to a genuinely high-quality business.</li>
          <li><strong>Stress test.</strong> If revenue dropped 30% in a recession, would the company still cover its interest? Lower D/E means more breathing room.</li>
        </ul>
        <p>
          Common <Link to="/blog/investing-mistakes-beginners-make">beginner mistakes</Link> often involve ignoring leverage when chasing high ROE or dividend yields.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
